import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { FileText, CheckCircle, XCircle, Clock, Phone } from "lucide-react";
import { toast } from "sonner";
import BulkApproveButton from "./BulkApproveButton";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

const formatDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch { return iso; }
};

const statusMap = {
  pending: { label: "بانتظار الموافقة", color: "#F5A623", icon: Clock },
  approved: { label: "تم القبض", color: "#7ED321", icon: CheckCircle },
  rejected: { label: "مرفوض", color: "#E85D45", icon: XCircle },
};

const DebtPaymentsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/debt-payments`);
      setItems(data);
    } catch {
      toast.error("فشل تحميل طلبات السداد");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleAction = async (id, action) => {
    const msg = action === "approve" ? "تأكيد قبض المبلغ وخصمه من دين الزبون؟" : "رفض هذا الطلب؟";
    if (!(await confirmDialog(msg))) return;
    setBusy(id);
    try {
      await axios.patch(`${API}/admin/debt-payments/${id}`, { action });
      toast.success(action === "approve" ? "تم قبض المبلغ وتنزيل الدين" : "تم رفض الطلب");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل");
    } finally {
      setBusy(null);
    }
  };

  const filtered = filter === "all" ? items : items.filter((w) => w.status === filter);
  const pending = items.filter((w) => w.status === "pending").length;

  return (
    <div data-testid="debt-payments-tab" className="space-y-4">
      <h2 className="font-heading font-bold text-xl text-[#111814] flex items-center gap-2">
        <FileText className="w-6 h-6 text-[#E85D45]" />
        طلبات سداد الدين
        {pending > 0 && (
          <span className="bg-[#E85D45] text-white text-xs px-2.5 py-0.5 rounded-full font-bold animate-pulse">
            {pending} جديد
          </span>
        )}
      </h2>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-2 overflow-x-auto">
          {[
            { key: "pending", label: "بانتظار" },
            { key: "approved", label: "تم القبض" },
            { key: "rejected", label: "مرفوض" },
            { key: "all", label: "الكل" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`dp-filter-${f.key}`}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                filter === f.key ? "bg-[#2D4A3E] text-white" : "bg-white border border-[#E4EBE6] text-[#5C6E64]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <BulkApproveButton resource="debt-payments" label="موافقة على الكل" count={pending} onDone={fetchData} />
      </div>

      {loading ? (
        <p className="text-center text-[#5C6E64] py-8">جاري التحميل...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-[#E4EBE6]">
          <FileText className="w-16 h-16 text-[#E4EBE6] mx-auto mb-3" />
          <p className="text-[#5C6E64]">لا توجد طلبات</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((w, idx) => {
            const Icon = statusMap[w.status]?.icon || Clock;
            const color = statusMap[w.status]?.color || "#999";
            return (
              <motion.div
                key={w.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
                data-testid={`dp-${w.id}`}
                className="bg-white border border-[#E4EBE6] rounded-2xl p-4 space-y-3"
              >
                <div className="flex justify-between items-start flex-wrap gap-2">
                  <div>
                    <p className="font-bold text-[#111814]">{w.market_name || w.user_name}</p>
                    <a href={`tel:${w.user_phone}`} className="text-sm text-[#E85D45] flex items-center gap-1 mt-1 hover:underline">
                      <Phone className="w-3 h-3" />{w.user_phone}
                    </a>
                    <p className="text-xs text-[#5C6E64] mt-1">{formatDate(w.created_at)}</p>
                  </div>
                  <span className="px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5" style={{ backgroundColor: `${color}20`, color }}>
                    <Icon className="w-3 h-3" />
                    {statusMap[w.status]?.label}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 bg-[#F3F6F4] rounded-xl p-3">
                  <div>
                    <p className="text-xs text-[#5C6E64]">مبلغ يريد دفعه</p>
                    <p className="font-bold text-xl text-[#7ED321]">{fmt(w.amount)} د.ع</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#5C6E64]">إجمالي دينه</p>
                    <p className="font-bold text-lg text-[#E85D45]">{fmt(w.debt_at_request)} د.ع</p>
                  </div>
                </div>

                {w.note && (
                  <div className="bg-[#FFF8E1] border border-[#F5A623]/30 rounded-xl px-3 py-2 text-sm italic">
                    {w.note}
                  </div>
                )}

                {w.status === "pending" && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleAction(w.id, "approve")}
                      disabled={busy === w.id}
                      data-testid={`dp-approve-${w.id}`}
                      className="flex-1 bg-[#7ED321] hover:bg-[#6cb91d] text-white font-bold py-2.5 rounded-full text-sm disabled:opacity-50"
                    >
                      ✓ تم قبض المبلغ
                    </button>
                    <button
                      onClick={() => handleAction(w.id, "reject")}
                      disabled={busy === w.id}
                      data-testid={`dp-reject-${w.id}`}
                      className="flex-1 bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold py-2.5 rounded-full text-sm disabled:opacity-50"
                    >
                      ✕ رفض
                    </button>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DebtPaymentsTab;
